import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ConfirmModal, confirmModal } from '@/components/ui/modal'
import { useDeleteRole, useSetRoleStatus } from '../api'
import type { Role } from '../types'
import { toastError } from '../ui'

export function useRoleCommands({ onDeleted }: { onDeleted?: (role: Role) => void } = {}) {
  const { t } = useTranslation()
  const setStatus = useSetRoleStatus()
  const remove = useDeleteRole()
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null)

  const toggleStatus = async (role: Role) => {
    const deactivate = role.status === 'active'
    const ok = await confirmModal({
      title: t(deactivate ? 'access.roles.deactivateTitle' : 'access.roles.activateTitle'),
      description: t(deactivate ? 'access.roles.deactivateBody' : 'access.roles.activateBody', { name: role.name, count: role.assignments.length }),
      confirmLabel: t(deactivate ? 'access.roles.actions.deactivate' : 'access.roles.actions.activate'),
      destructive: deactivate,
    })
    if (!ok) return
    try {
      await setStatus.mutateAsync({ id: role.id, status: deactivate ? 'passive' : 'active' })
      toast.success(t('access.roles.statusChanged'))
    } catch (e) {
      toastError(e)
    }
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await remove.mutateAsync(pendingDelete.id)
      toast.success(t('common.deleted'))
      onDeleted?.(pendingDelete)
      setPendingDelete(null)
    } catch (e) {
      toastError(e)
    }
  }

  const dialog = (
    <ConfirmModal
      open={!!pendingDelete}
      onOpenChange={(v) => !v && setPendingDelete(null)}
      title={t('access.roles.deleteTitle')}
      description={t('access.roles.deleteBody', { name: pendingDelete?.name ?? '', count: pendingDelete?.assignments.length ?? 0 })}
      confirmLabel={t('common.delete')}
      destructive
      loading={remove.isPending}
      onConfirm={() => void confirmDelete()}
    />
  )

  return {
    toggleStatus,
    requestDelete: setPendingDelete,
    busy: setStatus.isPending,
    dialog,
  }
}

export type RoleCommands = ReturnType<typeof useRoleCommands>
